/**
 * SaveProgressOverlay - Enhanced progress overlay for saving experiences
 * Shows step-by-step feedback with progress bars and calculated ETA
 */
export class SaveProgressOverlay {
    constructor() {
        this.overlay = null;
        this.currentStep = null;
        this.isVisible = false;
        this.initialize();
    }

    /**
     * Initialize the overlay DOM element
     */
    initialize() {
        // Create overlay container
        this.overlay = document.createElement('div');
        this.overlay.id = 'save-progress-overlay';
        this.overlay.className = 'fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-50 hidden';

        // Create content container
        this.overlay.innerHTML = `
            <div class="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 border-2 border-blue-500/30 shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300">
                <!-- Icon and Title -->
                <div class="flex items-center gap-4 mb-6">
                    <div class="relative">
                        <!-- Animated spinner ring -->
                        <div class="w-16 h-16 rounded-full border-4 border-blue-500/20"></div>
                        <div class="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
                        <!-- Center icon -->
                        <div class="absolute inset-0 flex items-center justify-center">
                            <svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
                            </svg>
                        </div>
                    </div>
                    <div class="flex-1">
                        <h3 class="text-2xl font-bold text-white mb-1">Sauvegarde en cours</h3>
                        <p class="text-sm text-slate-400" id="save-step-title">Préparation...</p>
                    </div>
                </div>

                <!-- Progress bar container -->
                <div class="mb-6">
                    <div class="flex items-center justify-between mb-2">
                        <span class="text-sm text-slate-300" id="save-progress-label">Initialisation</span>
                        <span class="text-sm font-mono text-blue-400" id="save-progress-percent">0%</span>
                    </div>
                    <div class="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div id="save-progress-bar" class="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300 ease-out" style="width: 0%"></div>
                    </div>
                </div>

                <!-- Step description -->
                <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <p class="text-sm text-slate-300 leading-relaxed" id="save-step-description">
                        Initialisation du processus de sauvegarde...
                    </p>
                </div>

                <!-- Image counter (shown during optimization) -->
                <div id="save-image-counter" class="mt-4 text-center text-sm text-slate-400 hidden">
                    <span id="save-images-done">0</span> / <span id="save-images-total">0</span> images traitées
                </div>
            </div>
        `;

        document.body.appendChild(this.overlay);
    }

    /**
     * Show the overlay
     */
    show() {
        if (this.overlay) {
            this.overlay.classList.remove('hidden');
            this.isVisible = true;
        }
    }

    /**
     * Hide the overlay
     */
    hide() {
        if (this.overlay) {
            this.overlay.classList.add('hidden');
            this.isVisible = false;
            this.currentStep = null;
        }
    }

    /**
     * Update the progress overlay with step information
     * @param {string} step - Current step identifier ('preparing', 'optimizing', 'complete', 'error')
     * @param {Object} options - Additional options
     * @param {number} options.progress - Progress percentage (0-100)
     * @param {string} options.title - Step title
     * @param {string} options.label - Progress label
     * @param {string} options.description - Step description
     * @param {number} options.imagesDone - Number of images processed
     * @param {number} options.imagesTotal - Total number of images
     */
    updateProgress(step, options = {}) {
        if (!this.overlay) return;

        this.currentStep = step;

        const {
            progress = 0,
            title = '',
            label = '',
            description = '',
            imagesDone = 0,
            imagesTotal = 0
        } = options;

        // Update elements
        const stepTitle = this.overlay.querySelector('#save-step-title');
        const progressLabel = this.overlay.querySelector('#save-progress-label');
        const progressPercent = this.overlay.querySelector('#save-progress-percent');
        const progressBar = this.overlay.querySelector('#save-progress-bar');
        const stepDescription = this.overlay.querySelector('#save-step-description');
        const imageCounter = this.overlay.querySelector('#save-image-counter');
        const imagesDoneEl = this.overlay.querySelector('#save-images-done');
        const imagesTotalEl = this.overlay.querySelector('#save-images-total');

        if (stepTitle) stepTitle.textContent = title;
        if (progressLabel) progressLabel.textContent = label;
        if (progressPercent) progressPercent.textContent = `${Math.round(progress)}%`;
        if (progressBar) progressBar.style.width = `${progress}%`;
        if (stepDescription) stepDescription.textContent = description;

        // Show/hide image counter based on step
        if (step === 'optimizing' && imageCounter) {
            imageCounter.classList.remove('hidden');
            if (imagesDoneEl) imagesDoneEl.textContent = imagesDone;
            if (imagesTotalEl) imagesTotalEl.textContent = imagesTotal;
        } else if (imageCounter) {
            imageCounter.classList.add('hidden');
        }
    }

    /**
     * Show step 1: Preparing JSON data
     */
    showPreparingStep() {
        this.show();
        this.updateProgress('preparing', {
            progress: 30,
            title: 'Étape 1/2',
            label: 'Préparation des données',
            description: 'Préparation de votre expérience pour la sauvegarde sur nos serveurs...'
        });
    }

    /**
     * Show step 2: Optimizing on server with progress
     * @param {number} imagesDone - Number of images processed
     * @param {number} imagesTotal - Total number of images
     */
    showOptimizingStep(imagesDone = 0, imagesTotal = 0) {
        const baseProgress = 40; // Start at 40%
        const progressRange = 55; // 40% -> 95%
        const imageProgress = imagesTotal > 0 ? (imagesDone / imagesTotal) * progressRange : 0;
        const totalProgress = baseProgress + imageProgress;

        this.updateProgress('optimizing', {
            progress: totalProgress,
            title: 'Étape 2/2',
            label: 'Optimisation en cours',
            description: `Optimisation de votre expérience sur nos serveurs... Traitement des images 360° pour des performances optimales.`,
            imagesDone,
            imagesTotal
        });
    }

    /**
     * Show completion step
     */
    showCompleteStep() {
        this.updateProgress('complete', {
            progress: 100,
            title: 'Terminé !',
            label: 'Sauvegarde réussie',
            description: 'Votre expérience a été sauvegardée avec succès sur nos serveurs.'
        });
    }

    /**
     * Show error step
     * @param {string} errorMessage - Error message to display
     */
    showErrorStep(errorMessage = 'Une erreur est survenue') {
        this.updateProgress('error', {
            progress: 0,
            title: 'Erreur',
            label: 'Échec de la sauvegarde',
            description: errorMessage
        });
    }

    /**
     * Dispose and cleanup
     */
    dispose() {
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
    }
}
